import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faHashtag, faSpinner, faEllipsisV, faArrowDown } from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';
import { useAuth } from '../../../contexts/AuthContext';

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return parts.map(p => p[0]).join('').toUpperCase();
  return parts[0][0].toUpperCase();
}

function formatTime(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return time;
  if (isYesterday) return `Hier ${time}`;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) + ` ${time}`;
}

const AVATAR_COLORS = ['#dc2626', '#2563eb', '#059669', '#7c3aed', '#d97706', '#0891b2', '#be185d', '#4338ca', '#0d9488', '#c2410c'];
function avatarColor(userId) { return AVATAR_COLORS[(userId || 0) % AVATAR_COLORS.length]; }

const POLL_INTERVAL = 5000;

// localStorage helpers for read tracking
function getStorageKey(projectId, channelKey) {
  return `project_${projectId}_read_${channelKey}`;
}

function channelKeyFromId(channelId) {
  // 'project' -> 0, 'slot-123' -> 123
  return channelId === 'project' ? '0' : channelId.replace('slot-', '');
}

const BriefTab = ({ projectId, slots, onUnreadChange, readOnly }) => {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState('project');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState('');
  const [showMembers, setShowMembers] = useState(false);
  const [members, setMembers] = useState([]);
  const [hasNewBelow, setHasNewBelow] = useState(false);
  const [unreadChannels, setUnreadChannels] = useState({}); // { channelId: true }

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const membersPanelRef = useRef(null);
  const menuBtnRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0 });
  const lastMessageIdRef = useRef(null);
  const pollingRef = useRef(false);

  useEffect(() => {
    const ch = [{ id: 'project', name: 'Projet', slotId: null }];
    (slots || []).forEach(s => { ch.push({ id: `slot-${s.id}`, name: s.name, slotId: s.id }); });
    setChannels(ch);
  }, [slots]);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/members`).then(res => setMembers(res.data.data || [])).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (!showMembers) return;
    const handleClick = (e) => {
      if (menuBtnRef.current && menuBtnRef.current.contains(e.target)) return;
      if (membersPanelRef.current && !membersPanelRef.current.contains(e.target)) setShowMembers(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setShowMembers(false); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [showMembers]);

  // Check if user is scrolled to bottom
  const isAtBottom = () => {
    const el = messagesContainerRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
    setHasNewBelow(false);
  };

  // Mark current channel as read in localStorage
  const markChannelRead = useCallback((channelId, latestId) => {
    if (!latestId || !projectId) return;
    const key = getStorageKey(projectId, channelKeyFromId(channelId));
    localStorage.setItem(key, String(latestId));
    setUnreadChannels(prev => {
      const next = { ...prev };
      delete next[channelId];
      return next;
    });
  }, [projectId]);

  // Full fetch (initial load or channel switch)
  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeChannel !== 'project') params.survey_slot_id = activeChannel.replace('slot-', '');
      const res = await api.get(`/projects/${projectId}/messages`, { params });
      const msgs = res.data.data || [];
      setMessages(msgs);
      const lastId = msgs.length > 0 ? msgs[msgs.length - 1].id : null;
      lastMessageIdRef.current = lastId;
      // Mark this channel as read when we load it
      if (lastId) markChannelRead(activeChannel, lastId);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [projectId, activeChannel, markChannelRead]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Scroll to bottom on initial load / channel switch
  useEffect(() => {
    if (!loading) {
      setTimeout(() => scrollToBottom(false), 50);
    }
  }, [loading, activeChannel]);

  // Poll for unread indicators across ALL channels
  useEffect(() => {
    if (!projectId) return;
    const checkUnread = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/messages/latest-per-channel`);
        const latest = res.data.data || {};
        const newUnread = {};
        let anyUnread = false;

        for (const [key, latestId] of Object.entries(latest)) {
          const channelId = key === '0' ? 'project' : `slot-${key}`;
          // Don't mark active channel as unread
          if (channelId === activeChannel) continue;
          const stored = parseInt(localStorage.getItem(getStorageKey(projectId, key)) || '0', 10);
          if (latestId > stored) {
            newUnread[channelId] = true;
            anyUnread = true;
          }
        }

        setUnreadChannels(newUnread);
        if (onUnreadChange) onUnreadChange(anyUnread);
      } catch {}
    };
    checkUnread();
    const intervalId = setInterval(checkUnread, POLL_INTERVAL);
    return () => clearInterval(intervalId);
  }, [projectId, activeChannel, onUnreadChange]);

  // Smart polling — only for the active channel's messages
  useEffect(() => {
    const poll = async () => {
      if (pollingRef.current) return;
      pollingRef.current = true;
      try {
        const params = { after: lastMessageIdRef.current || 0 };
        if (activeChannel !== 'project') params.survey_slot_id = activeChannel.replace('slot-', '');
        const res = await api.get(`/projects/${projectId}/messages`, { params });
        const newMsgs = res.data.data || [];

        if (newMsgs.length > 0) {
          setMessages(prev => {
            const existingIds = new Set(prev.filter(m => !String(m.id).startsWith('temp-')).map(m => m.id));
            const truly_new = newMsgs.filter(m => !existingIds.has(m.id));
            if (truly_new.length === 0) return prev;

            const updated = [...prev, ...truly_new];
            const lastId = updated[updated.length - 1].id;
            lastMessageIdRef.current = lastId;

            // Mark as read since user is viewing this channel
            markChannelRead(activeChannel, lastId);

            if (isAtBottom()) {
              setTimeout(() => scrollToBottom(), 50);
            } else {
              if (truly_new.some(m => m.user_id !== user?.id)) {
                setHasNewBelow(true);
              }
            }

            return updated;
          });
        }
      } catch { }
      finally { pollingRef.current = false; }
    };

    const intervalId = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(intervalId);
  }, [projectId, activeChannel, user?.id, markChannelRead]);

  const handleSend = async () => {
    if (!draft.trim() || sending) return;
    const text = draft.trim();
    setDraft('');
    setSending(true);

    const tempMsg = {
      id: `temp-${Date.now()}`, message: text,
      user: { id: user.id, name: user.name, role: user.role },
      user_id: user.id, created_at: new Date().toISOString(), _sending: true,
    };
    setMessages(prev => [...prev, tempMsg]);
    setTimeout(() => scrollToBottom(), 50);

    try {
      const payload = { message: text };
      if (activeChannel !== 'project') payload.survey_slot_id = parseInt(activeChannel.replace('slot-', ''));
      const res = await api.post(`/projects/${projectId}/messages`, payload);
      const savedMsg = res.data.data;
      setMessages(prev => prev.map(m => m.id === tempMsg.id ? savedMsg : m));
      lastMessageIdRef.current = savedMsg.id;
      markChannelRead(activeChannel, savedMsg.id);
    } catch {
      setMessages(prev => prev.map(m => m.id === tempMsg.id ? { ...m, _sending: false, _failed: true } : m));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleRetry = async (failedMsg) => {
    setMessages(prev => prev.map(m => m.id === failedMsg.id ? { ...m, _failed: false, _sending: true } : m));
    try {
      const payload = { message: failedMsg.message };
      if (activeChannel !== 'project') payload.survey_slot_id = parseInt(activeChannel.replace('slot-', ''));
      const res = await api.post(`/projects/${projectId}/messages`, payload);
      const savedMsg = res.data.data;
      setMessages(prev => prev.map(m => m.id === failedMsg.id ? savedMsg : m));
      lastMessageIdRef.current = savedMsg.id;
      markChannelRead(activeChannel, savedMsg.id);
    } catch {
      setMessages(prev => prev.map(m => m.id === failedMsg.id ? { ...m, _sending: false, _failed: true } : m));
    }
  };

  const handleDismissFailed = (msgId) => {
    setMessages(prev => prev.filter(m => m.id !== msgId));
  };

  const handleChannelSwitch = (channelId) => {
    setActiveChannel(channelId);
    setHasNewBelow(false);
  };

  const activeChannelObj = channels.find(c => c.id === activeChannel) || channels[0];

  return (
    <div className="brief-container">
      {/* Sidebar — desktop */}
      <div className="brief-sidebar">
        <div style={{ padding: '12px 14px', fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Canaux
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {channels.map(ch => {
            const isActive = activeChannel === ch.id;
            const hasUnread = !!unreadChannels[ch.id];
            return (
              <div key={ch.id} onClick={() => handleChannelSwitch(ch.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', cursor: 'pointer',
                  background: isActive ? '#fff' : 'transparent',
                  borderLeft: isActive ? '3px solid #dc2626' : '3px solid transparent',
                }}>
                <FontAwesomeIcon icon={faHashtag} size="xs" style={{ color: isActive ? '#dc2626' : '#d1d5db' }} />
                <span title={ch.name} style={{
                  fontSize: '0.75rem', fontWeight: (isActive || hasUnread) ? 600 : 400,
                  color: isActive ? '#111827' : hasUnread ? '#111827' : '#6b7280',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                }}>
                  {ch.name}
                </span>
                {hasUnread && (
                  <span style={{
                    width: 8, height: 8, borderRadius: '50%', background: '#dc2626',
                    flexShrink: 0, animation: 'channelUnreadPulse 1.5s ease-in-out infinite',
                  }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Channel strip — mobile only */}
      <div className="brief-channel-strip">
        {channels.map(ch => {
          const isActive = activeChannel === ch.id;
          const hasUnread = !!unreadChannels[ch.id];
          return (
            <button key={ch.id} onClick={() => handleChannelSwitch(ch.id)}
              className={`brief-channel-chip${isActive ? ' active' : ''}`}>
              <FontAwesomeIcon icon={faHashtag} size="xs" />
              {ch.name}
              {hasUnread && <span className="brief-channel-chip-dot" />}
            </button>
          );
        })}
      </div>

      {/* Chat */}
      <div style={{ display: 'flex', flexDirection: 'column', position: 'relative', minHeight: 0, flex: 1 }}>
        {/* Header */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 6 }}>
          <FontAwesomeIcon icon={faHashtag} size="xs" style={{ color: '#9ca3af' }} />
          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', flex: 1 }}>{activeChannelObj?.name}</span>
          <button
            ref={menuBtnRef}
            onClick={() => {
              if (!showMembers && menuBtnRef.current) {
                const rect = menuBtnRef.current.getBoundingClientRect();
                setPanelPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
              }
              setShowMembers(prev => !prev);
            }}
            style={{ background: 'none', border: 'none', color: showMembers ? '#111827' : '#9ca3af', cursor: 'pointer', padding: '4px 6px', borderRadius: 4 }}>
            <FontAwesomeIcon icon={faEllipsisV} />
          </button>
        </div>

        {/* Messages */}
        <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', background: '#fafafa' }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40, color: '#9ca3af' }}><FontAwesomeIcon icon={faSpinner} spin /></div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 20px' }}>
              <div style={{ fontSize: '0.82rem', color: '#d1d5db' }}>Aucun message</div>
              <div style={{ fontSize: '0.7rem', color: '#e5e7eb', marginTop: 4 }}>Commencez la discussion</div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => {
                const uid = msg.user_id || msg.user?.id;
                const isMe = uid === user?.id;
                const prevMsg = messages[i - 1];
                const prevUid = prevMsg ? (prevMsg.user_id || prevMsg.user?.id) : null;
                const isGrouped = prevUid === uid;
                const color = avatarColor(uid);

                return (
                  <div key={msg.id} style={{
                    display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row',
                    gap: 8, marginTop: isGrouped ? 3 : (i > 0 ? 14 : 0),
                    alignItems: 'flex-end',
                    opacity: msg._sending ? 0.5 : 1,
                  }}>
                    <div style={{ width: 30, flexShrink: 0 }}>
                      {!isGrouped && (
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%', background: color, color: '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.55rem', fontWeight: 700,
                        }}>
                          {getInitials(msg.user?.name)}
                        </div>
                      )}
                    </div>
                    <div style={{ maxWidth: '68%', minWidth: 0 }}>
                      {!isGrouped && (
                        <div style={{
                          display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3,
                          justifyContent: isMe ? 'flex-end' : 'flex-start',
                        }}>
                          <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#555' }}>{msg.user?.name}</span>
                          <span style={{ fontSize: '0.55rem', color: '#bbb' }}>{formatTime(msg.created_at)}</span>
                        </div>
                      )}
                      <div style={{
                        padding: '8px 14px',
                        background: msg._failed ? '#fef2f2' : isMe ? '#f0f0f0' : '#1e1e1e',
                        color: msg._failed ? '#991b1b' : isMe ? '#1a1a1a' : '#f5f5f5',
                        border: msg._failed ? '1px solid #fecaca' : 'none',
                        fontSize: '0.8rem', lineHeight: 1.55,
                        borderRadius: isMe ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                        wordBreak: 'break-word',
                      }}>
                        {msg.message}
                      </div>
                      {msg._failed && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 4, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                          <span style={{ fontSize: '0.6rem', color: '#ef4444' }}>Non envoy&eacute;</span>
                          <button onClick={() => handleRetry(msg)} style={{ fontSize: '0.6rem', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>R&eacute;essayer</button>
                          <button onClick={() => handleDismissFailed(msg.id)} style={{ fontSize: '0.6rem', color: '#9ca3af', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Supprimer</button>
                        </div>
                      )}
                      {isGrouped && (
                        <div style={{ fontSize: '0.5rem', color: '#ccc', marginTop: 2, textAlign: isMe ? 'right' : 'left' }}>
                          {formatTime(msg.created_at)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* New messages indicator */}
        {hasNewBelow && (
          <div
            onClick={() => scrollToBottom()}
            style={{
              position: 'absolute', bottom: 60, left: '50%', transform: 'translateX(-50%)',
              background: '#dc2626', color: '#fff', padding: '5px 14px', borderRadius: 20,
              fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex', alignItems: 'center', gap: 5,
              animation: 'newMsgIn 0.2s ease',
            }}
          >
            <FontAwesomeIcon icon={faArrowDown} size="xs" /> Nouveaux messages
          </div>
        )}

        {/* Input */}
        {!readOnly && <div style={{ padding: '10px 16px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 8, background: '#fff' }}>
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder={`Message dans #${activeChannelObj?.name || 'Projet'}...`}
            style={{
              flex: 1, fontSize: '0.8rem', padding: '9px 14px',
              border: '1px solid #e5e7eb', borderRadius: 8, outline: 'none',
            }}
            onFocus={e => e.target.style.borderColor = '#9ca3af'}
            onBlur={e => e.target.style.borderColor = '#e5e7eb'}
          />
          <button onClick={handleSend} disabled={!draft.trim() || sending}
            style={{
              padding: '9px 14px', borderRadius: 8, border: 'none',
              background: draft.trim() ? '#dc2626' : '#f3f4f6',
              color: draft.trim() ? '#fff' : '#d1d5db',
              cursor: draft.trim() ? 'pointer' : 'default',
              transition: 'all 0.15s',
            }}>
            <FontAwesomeIcon icon={faPaperPlane} size="sm" />
          </button>
        </div>}
      </div>

      {/* Members panel */}
      {showMembers && (
        <div ref={membersPanelRef} style={{
          position: 'fixed', top: panelPos.top, right: panelPos.right, zIndex: 9998,
          width: 300, maxHeight: 420, background: '#fff',
          border: '1px solid #e5e7eb', borderRadius: 10,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column',
          animation: 'membersPanelIn 0.15s ease', overflow: 'hidden',
        }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{
              width: 34, height: 34, borderRadius: '50%', background: avatarColor(user?.id || 0), color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
            }}>
              {getInitials(user?.name)}
            </div>
            <div>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#111827' }}>{user?.name}</div>
              <div style={{ fontSize: '0.62rem', color: '#9ca3af' }}>Vous</div>
            </div>
          </div>
          <div style={{ padding: '10px 16px 6px', fontSize: '0.62rem', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>
            Membres · {members.length}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 10px' }}>
            {members.map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 6 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', background: avatarColor(m.id), color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.52rem', fontWeight: 700, flexShrink: 0,
                }}>
                  {getInitials(m.name)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 500, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                  <div style={{ fontSize: '0.58rem', color: '#9ca3af' }}>{m.role_display || m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <style>{`
        @keyframes membersPanelIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }
        @keyframes newMsgIn { from { opacity:0; transform:translateX(-50%) translateY(10px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }
        @keyframes channelUnreadPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.7); }
        }
        .brief-container {
          display: grid; grid-template-columns: 200px 1fr; gap: 0;
          height: calc(100vh - 320px); min-height: 400px;
          border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; background: #fff;
        }
        .brief-sidebar {
          border-right: 1px solid #e5e7eb; display: flex; flex-direction: column; background: #f9fafb; min-height: 0;
        }
        .brief-channel-strip {
          display: none;
        }
        .brief-channel-chip {
          display: flex; align-items: center; gap: 5px; padding: 6px 12px;
          border: 1px solid #e5e7eb; border-radius: 20px; background: #fff;
          color: #6b7280; font-size: 0.72rem; font-weight: 500;
          cursor: pointer; white-space: nowrap; position: relative;
          transition: all 0.12s;
        }
        .brief-channel-chip.active {
          background: #dc2626; color: #fff; border-color: #dc2626;
        }
        .brief-channel-chip-dot {
          width: 6px; height: 6px; border-radius: 50%; background: #dc2626; flex-shrink: 0;
        }
        .brief-channel-chip.active .brief-channel-chip-dot {
          background: #fff;
        }
        @media (max-width: 768px) {
          .brief-container {
            display: flex; flex-direction: column;
            height: calc(100vh - 300px); min-height: 350px;
          }
          .brief-sidebar { display: none; }
          .brief-channel-strip {
            display: flex; gap: 6px; padding: 10px 12px;
            overflow-x: auto; flex-shrink: 0;
            border-bottom: 1px solid #e5e7eb; background: #fafafa;
          }
          .brief-channel-strip::-webkit-scrollbar { height: 0; }
        }
      `}</style>
    </div>
  );
};

export default BriefTab;
